import DOMPurify from "dompurify";
import { marked } from "marked";

/**
 * Renders a note body. Markdown comes from the user, so the HTML is sanitised; task-list boxes are
 * left enabled so the reader can tick them, and the click is written back into the Markdown.
 */
export function renderNote(markdown: string): string {
  const html = marked.parse(markdown, { gfm: true, breaks: true, async: false });
  const clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "form", "button", "iframe", "object", "embed"],
    ADD_ATTR: ["checked"],
  });
  return clean.replace(/<input([^>]*?) disabled=""([^>]*)>/g, "<input$1$2>");
}

const TASK_LINE = /^(\s*(?:[-*+]|\d+[.)])\s+)\[( |x|X)\]/;

/** Flips the n-th task-list box in the Markdown, skipping fenced code blocks. */
export function toggleChecklist(markdown: string, index: number): string {
  const lines = markdown.split("\n");
  let seen = 0;
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = TASK_LINE.exec(line);
    if (!match) continue;
    if (seen === index) {
      const mark = match[2] === " " ? "x" : " ";
      lines[i] = line.replace(TASK_LINE, `${match[1]}[${mark}]`);
      return lines.join("\n");
    }
    seen += 1;
  }
  return markdown;
}

/** File name for an exported note: the title made safe for every file system. */
export function exportFileName(title: string): string {
  const base = title
    .trim()
    .replace(/[\\/:*?"<>|\p{Cc}]+/gu, " ")
    .replace(/\s+/g, " ")
    .slice(0, 80)
    .trim();
  return `${base || "note"}.md`;
}
